import min from 'date-fns/min'
import max from 'date-fns/max'
import addDays from 'date-fns/addDays'
import addWeeks from 'date-fns/addWeeks'
import addMonths from 'date-fns/addMonths'
import addYears from 'date-fns/addYears'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'

interface NormalizedDateSequenceItemsOptions<Item extends Record<string, any>> {
  startDate?: Date
  endDate?: Date
  groupType?: 'daily' | 'weekly' | 'monthly' | 'yearly'
  dateTimeKey?: keyof Item
}

export default function normalizeDateSequenceItems<Item extends Record<string, any>>(
  items: Item[],
  fill: (dateString: string) => Item,
  {
    startDate,
    endDate,
    groupType = 'daily',
    dateTimeKey = 'dateTime' as keyof Item,
  }: NormalizedDateSequenceItemsOptions<Item>,
): Item[] {
  if (!startDate || !endDate) {
    const dates = items.map(item => new Date(item[dateTimeKey]))
    if (!startDate) startDate = min(dates)
    if (!endDate) endDate = max(dates)
  }

  let currentDate = startDate
  const result: Item[] = []
  const formatByGroupType = {
    daily: 'yyyy-MM-dd',
    weekly: "yyyy-'w'II",
    monthly: 'yyyy-MM',
    yearly: 'yyyy',
  }

  while (currentDate <= endDate) {
    const dateFormat = formatByGroupType[groupType]
    const dateString = format(currentDate, dateFormat)

    let item = items.find(item => format(parseISO(item[dateTimeKey]), dateFormat) === dateString)

    if (!item) {
      item = fill(dateString)
    }

    result.push(item)

    currentDate = addInterval(currentDate, groupType)
  }

  return result
}

function addInterval(date: Date, groupType: 'daily' | 'weekly' | 'monthly' | 'yearly'): Date {
  switch (groupType) {
    case 'daily':
      return addDays(date, 1)
    case 'weekly':
      return addWeeks(date, 1)
    case 'monthly':
      return addMonths(date, 1)
    case 'yearly':
      return addYears(date, 1)
    default:
      throw new Error('Invalid groupType')
  }
}
